<template>
  <aha-modal
    id="ai-generate-slides-modal"
    :size="modalSize"
    :title="$i18nPresenter.t('AI-Generated Slides')"
    :iconBeforeModalTitle="iconBeforeModalTitle"
    boxShadow="0 0 1px 8px rgba(255,255,255,0.3)"
    @hide="closeModal()"
    noCloseOnBackdrop
    :classesModal="{'ai-generate-slides__result-modal': true}"
  >
    <div class="ai-result__header" id="ai-result__header"></div>
    <div class="ai-result__wrapper" id="ai-slides-generator-result-layout">
      <div v-for="(response, index) in aiResponse" :key="response.id">
        <AIGeneratedPickAnswerContent
          v-if="response.type === AIResponseType.PICK_ANSWER"
          :response="response"
          :index="index"
          @handleAddSlideToPresentation="handleAddSlideToPresentation"
          class="content-item"
        />
        <AIGeneratedFreestyleContent
          v-if="response.type === AIResponseType.FREESTYLE"
          :response="response"
          :index="index"
          @handleAddSlideToPresentation="handleAddSlideToPresentation"
          class="content-item"
        />
        <AIGeneratedTypeAnswerContent
          v-if="response.type === AIResponseType.TYPE_ANSWER"
          :response="response"
          :index="index"
          @handleAddSlideToPresentation="handleAddSlideToPresentation"
          class="content-item"
        />
      </div>
      <aha-spinner
        v-if="!isDoneGenerating && !isSendingAIRequestFeedback"
        class="mb-4"
        :class="{'hidden': isLoadingSpinnerHidden}"
      ></aha-spinner>
      <div v-if="systemError" class="text-center mt-15">
        <AIStumped />
        <aha-button
          variant="base"
          @click.stop="retryAISlidesGeneratorAction('Retry')"
          class="mt-4"
          v-aha-emit-action
          name="ai_slides_generator_retry_button"
        >
          <aha-icon
            name="reset"
            width="15"
            height="15"
            viewBox="0 0 16 16"
            class="mr-1"
          />
          {{ $i18nPresenter.t('Retry') }}
        </aha-button>
      </div>
      <AIFeedbackLoading v-if="isSendingAIRequestFeedback" :resetProgressBar="loadingProgressBar" />
    </div>
    <div class="ai-result__bottom"></div>
    <AIFeedbackButtons
      @handleFeedbackQuiz="clickOnFeedbackButton"
      :isDoneGenerating="isDoneGenerating"
      :allFeedbackButtonTexts="allFeedbackButtonTexts"
      isDocToQuizModal
    />
  </aha-modal>
</template>

<script>
// utils
import { mapState, mapActions, mapGetters } from 'vuex'

// constants
import openAIConstants from '@/constant/openAI'

// components
// import FakeProgressbar from '../FakeProgressbar.vue'
import AIGeneratedPickAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedPickAnswerContent.vue'
import AIGeneratedFreestyleContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedFreestyleContent.vue'
import AIGeneratedTypeAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedTypeAnswerContent.vue'
import AIStumped from '@/components-v2/presentation-editor/new-slide/OpenAI/AIStumped.vue'
import AIFeedbackLoading from '@/components-v2/openAI/AIFeedbackLoading.vue'
import AIResponseType from '@/constant/aiResponseType'
import AIFeedbackButtons from '@/components-v2/openAI/AIFeedbackButtons.vue'

export default {
  components: {
    // FakeProgressbar,
    AIGeneratedPickAnswerContent,
    AIGeneratedFreestyleContent,
    AIGeneratedTypeAnswerContent,
    AIStumped,
    AIFeedbackLoading,
    AIFeedbackButtons,
  },
  props: {
    isDoneGenerating: {
      type: Boolean,
      default: false,
    },
    hasStoppedGenerating: {
      type: Boolean,
      default: false,
    },
    isSendingAIRequest: {
      type: Boolean,
      default: false,
    },
    isSendingAIRequestFeedback: {
      type: Boolean,
      default: false,
    },
    loadingProgressBar: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      AIResponseType,
      allFeedbackButtonTexts: openAIConstants.aiSlidesGeneratorFeedbacks,
    }
  },
  computed: {
    ...mapGetters('promptToSlideGenerator', [
      'promptMaxLength',
      'aiResponse', 'systemError', 'isLoadingSpinnerHidden',
      'isReturnedSlideError', 'customAISlideStumpedText']),
    ...mapGetters(['currentPresenterLanguage']),

    ...mapState({
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
    }),

    iconBeforeModalTitle() {
      return {
        name: 'sparkles',
        width: 20,
        height: 20,
        viewBox: '0 0 20 20 ',
        maintainColor: true,
      }
    },
    isFrenchOrVietnameseUser() {
      return this.currentPresenterLanguage === 'fr' || this.currentPresenterLanguage === 'vi'
    },
    modalSize() {
      return this.isFrenchOrVietnameseUser ? 'lg' : 'md'
    },
    isGeneratingSlides() {
      return this.isSendingAIRequest && !this.isSendingAIRequestFeedback && !this.systemError
    },
    isNotGeneratingAndHasSlides() {
      return !this.isSendingAIRequest && this.aiResponse
    },
    isSingleSlideGenerationError() {
      return this.isReturnedSlideError && !this.systemError
    },
    isContentListBottomVisible() {
      return this.isSingleSlideGenerationError || !this.isDoneGenerating || this.hasStoppedGenerating
    },
  },
  methods: {
    ...mapActions('promptToSlideGenerator', [
      'setAIResultModalVisibleAction',
      'stopGeneratingSlidesAction',
      'retryAISlidesGeneratorAction',
    ]),
    handleAddSlideToPresentation(response) {
      this.$emit('clickOnAddSlideButton', response, 'promptToSlideGenerator')
    },
    closeModal() {
      this.setAIResultModalVisibleAction(false)
      this.stopGeneratingSlidesAction()
    },
    clickOnFeedbackButton(feedback) {
      this.$emit('clickOnFeedbackButton', feedback)
    },
  },
}
</script>

<style>
.ai-generate-slides__result-modal {
  .aha-modal__header {
    @apply bg-primary-50 absolute top-0 w-full z-1;
    .aha-modal__header--text {
      @apply mt-0;
    }
  }
  .aha-modal__body {
    @apply bg-primary-50 p-0 m-0 relative;
    height: unset;
    overflow-x: visible;
    .aha-modal__header--text {
      @apply mt-0;
    }
  }
  .ai-result__header {
    height: 56px;
  }
  .ai-result__wrapper {
    @apply rounded text-base m-0 overflow-y-auto py-0 px-6;
    height: 80vh;
    @media (max-width: 768px) {
      height: 70vh;
    }
  }
  .ai-result__bottom {
    height: 53px;
  }
  .content-item {
    @apply shadow rounded-lg my-4;
    @apply w-full;
    color: #000;
    background-color: #fff;
    &.is-first-content-item {
      margin-top: 0;
    }
    &::v-deep {
      ul {
        list-style: disc;
        padding-left: 1rem;
      }
      p:not(:last-child) {
        padding-bottom: 0.75rem;
      }
    }
  }
  .feedback-loading {
    @apply flex items-center justify-center;
    .feedback-loading__content {
      margin-top: 1.5rem;
    }
  }
}
</style>
